import { useRouter } from 'next/router'
import React, { useMemo } from 'react'
import { GridBlockProvider } from '../../contexts'
import { IPage, IsImageCounterComponent, isSeparator } from '../../types'
import { isStoriesBlock } from '../../types/stories'
import { Breadcrumbs } from '../Breadcrumbs/Breadcrumbs'
import { PageTitle } from '../PageTitle/PageTitle'
import { PocTaboolaPageType, PocTaboolaSuffixId, Taboola } from '../Taboola'
import { Container } from './AuthorCategoryTag.styles'
import { EditMe } from '../../sharedComponents'
import dynamic from 'next/dynamic'
import { clientConfig } from '../../client'
import { PageGridItemToComponent } from '../../utils/ItemToComponent'

const TaboolaWidget = dynamic(() => import('../GridBlocks/TaboolaWidget'), {
  ssr: true,
})

type Props = {
  page: IPage
  isHomepage?: boolean
}

export const AuthorCategoryTag = ({
  page: {
    SEO,
    PageMeta,
    Header,
    Content: { PageGrid },
  },
  isHomepage,
}: Props) => {
  const { asPath } = useRouter()
  const pageType = useMemo(() => PageMeta.pageType, [PageMeta.pageType])
  const disableTaboola = useMemo(
    () => PageMeta.ads.block.taboola,
    [PageMeta.ads.block.taboola]
  )

  const taboolaType = useMemo(() => {
    const slugs = asPath.split('/').filter(slug => !!slug)
    const isNewsHp = slugs[0] === 'news'
    if (isHomepage) {
      return PocTaboolaPageType.home
    }
    if (isNewsHp) {
      return PocTaboolaPageType.news_hp
    }
    return PocTaboolaPageType.category
  }, [isHomepage, asPath])

  const items = useMemo(() => {
    let blocksCount = 0
    const blocks: JSX.Element[] = []
    const blocksLength = PageGrid?.length || 0
    const gridTypeToSkip = ['Separator']
    if (clientConfig.alsoSkipSpecialRef) {
      gridTypeToSkip.push('SpecialRef')
    }
    PageGrid?.map((item, index) => {
      if (!(item.grid_type && gridTypeToSkip.includes(item.grid_type))) {
        blocksCount++
      }
      const name = item?.newsroom_name || 'newsroom_' + item.grid_id
      const addNewsRoomWrapper =
        isHomepage &&
        !isSeparator(item) &&
        !IsImageCounterComponent(item) &&
        name !== 'mid_box'
      let grid_title = ''
      if ('grid_title' in item) {
        grid_title = item.grid_title?.text || ''
      }
      blocks.push(
        <EditMe key={`wp-${index}-${item.grid_id || 0}`} grid_id={item.grid_id}>
          <GridBlockProvider
            gridId={item.grid_id}
            pageId={PageMeta.id}
            index={index}
            title={grid_title}
          >
            {addNewsRoomWrapper ? (
              <div
                data-tb-region={name}
                data-shorts={isStoriesBlock(item) ? item.grid_id : undefined}
              >
                {PageGridItemToComponent(item, PageMeta, index + 1)}
              </div>
            ) : (
              PageGridItemToComponent(item, PageMeta, index + 1)
            )}
          </GridBlockProvider>
        </EditMe>
      )
      let pushTaboola = false
      if (!disableTaboola && blocksLength > 2) {
        if (pageType === 'grid') {
          if (blocksLength > 7) {
            if (index === blocksLength - 4) {
              pushTaboola = true
            }
          } else if (blocksLength > 5) {
            if (index === blocksLength - 3) {
              pushTaboola = true
            }
          } else {
            if (index === 2) {
              pushTaboola = true
            }
          }
        }
        if (isHomepage) {
          if (blocksCount === 5) {
            pushTaboola = true
          }
          if (
            blocksLength > 9 &&
            index === blocksLength + 1 - 6 &&
            blocks[index - 1].key !== 'TaboolaWrapper'
          ) {
            blocks.push(
              <TaboolaWidget
                key={'TaboolaWidget2'}
                taboolaType={taboolaType}
                index={2}
              />
            )
          }
        }
      }
      if (pushTaboola) {
        blocksCount++
        blocks.push(
          <TaboolaWidget
            key={'TaboolaWidget1'}
            taboolaType={taboolaType}
            index={1}
          />
        )
      }
    })
    return blocks
  }, [PageGrid, disableTaboola, isHomepage, pageType, taboolaType, PageMeta])

  if (!PageMeta || !PageGrid || !Header) {
    return null
  }
  return (
    <Container
      theme={{
        haseWarComp: PageMeta.war_comp?.showWarRail,
        isFaceLift: PageMeta.isFaceLift,
      }}
    >
      {!isHomepage && (
        <>
          <PageTitle SEO={SEO} pageMeta={PageMeta} />
          <Breadcrumbs breadcrumbs={Header.breadCrumbs} />
        </>
      )}
      {items}
      {taboolaType && !disableTaboola && (
        <Taboola
          key={taboolaType}
          pageType={taboolaType}
          suffixId={PocTaboolaSuffixId.pageFooter}
        />
      )}
    </Container>
  )
}
